import { SHORTCUT_CATALOG } from "../settings/shortcutCatalog.js";
import { shortcutFromKeyboardEvent } from "../settings/keyboardChord.js";
import { isChordAvailable } from "../settings/keyboardShortcuts.js";

export type ShortcutMapperSavePayload = Readonly<Partial<Record<string, string>>>;

export function openShortcutMapperDialog(opts: {
  initialOverrides: ShortcutMapperSavePayload;
  onSave: (overrides: ShortcutMapperSavePayload) => void;
}): void {
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog npp-dialog-shortcut-mapper";

  const draft: Partial<Record<string, string>> = { ...opts.initialOverrides };
  let captureListener: ((ev: KeyboardEvent) => void) | null = null;

  const stopRecording = (): void => {
    if (captureListener) {
      window.removeEventListener("keydown", captureListener, true);
      captureListener = null;
    }
    tbody.querySelectorAll("[data-recording]").forEach((el) => el.removeAttribute("data-recording"));
  };

  const effectiveChord = (commandId: string): string => {
    const row = SHORTCUT_CATALOG.find((r) => r.commandId === commandId)!;
    const o = draft[commandId];
    return (o !== undefined ? o : row.defaultChord).trim();
  };

  const rowsHtml = SHORTCUT_CATALOG.map(
    (r) => `
    <tr data-cmd="${r.commandId}">
      <td class="npp-sc-map-label">${escapeHtml(r.label)}</td>
      <td class="npp-sc-map-chord" data-role="chord"></td>
      <td class="npp-sc-map-actions">
        <button type="button" class="npp-sc-map-change" data-action="change">Change…</button>
        <button type="button" class="npp-sc-map-reset" data-action="reset">Reset</button>
      </td>
    </tr>`,
  ).join("");

  dlg.innerHTML = `
    <div class="npp-dialog-form">
      <h2 class="npp-dialog-title">Shortcut Mapper</h2>
      <p class="npp-dialog-hint">Click <strong>Change…</strong>, then press the new shortcut. Press <kbd>Esc</kbd> to cancel capture. Conflicts with another command are rejected.</p>
      <div class="npp-sc-map-scroll">
        <table class="npp-sc-map-table">
          <thead><tr><th>Command</th><th>Shortcut</th><th></th></tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
      <div class="npp-dialog-actions">
        <button type="button" value="reset-all">Reset all to defaults</button>
        <button type="button" value="ok">OK</button>
        <button type="button" value="cancel">Cancel</button>
      </div>
    </div>`;

  document.body.append(dlg);
  const tbody = dlg.querySelector("tbody")!;

  const refreshChordCells = (): void => {
    for (const tr of tbody.querySelectorAll<HTMLTableRowElement>("tr[data-cmd]")) {
      const id = tr.dataset.cmd!;
      const cell = tr.querySelector<HTMLElement>('[data-role="chord"]')!;
      cell.textContent = effectiveChord(id);
    }
  };

  const startRecording = (commandId: string): void => {
    stopRecording();
    const tr = [...tbody.querySelectorAll<HTMLTableRowElement>("tr[data-cmd]")].find(
      (row) => row.dataset.cmd === commandId,
    );
    tr?.setAttribute("data-recording", "true");
    const cell = tr?.querySelector<HTMLElement>('[data-role="chord"]');
    if (cell) cell.textContent = "Press keys…";

    captureListener = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        ev.stopPropagation();
        stopRecording();
        refreshChordCells();
        return;
      }
      if (["Control", "Shift", "Alt", "Meta"].includes(ev.key)) return;
      const chord = shortcutFromKeyboardEvent(ev);
      if (!chord) return;
      ev.preventDefault();
      ev.stopPropagation();

      if (!isChordAvailable(chord, draft, commandId)) {
        void alert(`"${chord}" is already assigned to another command.`);
        stopRecording();
        refreshChordCells();
        return;
      }

      const def = SHORTCUT_CATALOG.find((r) => r.commandId === commandId)!.defaultChord.trim();
      if (chord === def) delete draft[commandId];
      else draft[commandId] = chord;

      stopRecording();
      refreshChordCells();
    };
    window.addEventListener("keydown", captureListener, true);
  };

  tbody.addEventListener("click", (ev) => {
    const t = ev.target as HTMLElement;
    const btn = t.closest("button");
    if (!btn) return;
    const tr = btn.closest("tr[data-cmd]") as HTMLTableRowElement | null;
    const cmd = tr?.dataset.cmd;
    if (!cmd) return;
    const action = btn.dataset.action;
    if (action === "change") startRecording(cmd);
    if (action === "reset") {
      delete draft[cmd];
      refreshChordCells();
    }
  });

  dlg.querySelector<HTMLButtonElement>('button[value="reset-all"]')!.addEventListener("click", () => {
    for (const k of Object.keys(draft)) delete draft[k];
    stopRecording();
    refreshChordCells();
  });

  dlg.querySelector<HTMLButtonElement>('button[value="ok"]')!.addEventListener("click", () => {
    stopRecording();
    const next: Partial<Record<string, string>> = {};
    for (const row of SHORTCUT_CATALOG) {
      const v = draft[row.commandId];
      if (v !== undefined && v.trim() !== "" && v.trim() !== row.defaultChord.trim()) {
        next[row.commandId] = v.trim();
      }
    }
    opts.onSave(next);
    dlg.close();
  });

  dlg.querySelector<HTMLButtonElement>('button[value="cancel"]')!.addEventListener("click", () => {
    stopRecording();
    dlg.close();
  });

  dlg.addEventListener("close", () => {
    stopRecording();
    dlg.remove();
  });

  refreshChordCells();
  dlg.showModal();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

